import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDateForContract } from "@/shared/utils";
import type { WorkerListItem } from "@/features/hr/employees";
import type { CreateLeaveFormValues } from "../validation";
import LeavePreviewLayout from "./LeavePreviewLayout";

interface Props {
  employee: WorkerListItem;
  formData: CreateLeaveFormValues & { diagnosis?: string };
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  isPreviewOnly?: boolean;
}

export default function MedicalLeavePreview({
  employee,
  formData,
  onClose,
  onSubmit,
  isLoading,
  isPreviewOnly = false,
}: Props) {
  const { t, i18n } = useTranslation("LeaveApplicationsPage");

  const dateLocale = i18n.language === "kk" ? "kk" : "ru";

  const startDateText = useMemo(
    function () {
      if (!formData.start_date) return "_____________";
      return formatDateForContract(formData.start_date, dateLocale);
    },
    [formData.start_date, dateLocale],
  );

  const endDateText = useMemo(
    function () {
      if (!formData.end_date) return "_____________";
      return formatDateForContract(formData.end_date, dateLocale);
    },
    [formData.end_date, dateLocale],
  );

  const daysCount = useMemo(
    function () {
      if (!formData.start_date || !formData.end_date) return 0;
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    },
    [formData.start_date, formData.end_date],
  );

  const diagnosis = formData.diagnosis || "_____________";

  return (
    <LeavePreviewLayout
      employee={employee}
      formData={formData}
      onClose={onClose}
      onSubmit={onSubmit}
      isLoading={isLoading}
      isPreviewOnly={isPreviewOnly}
    >
      <p>
        {t("preview.medical.requestText")} <strong>{daysCount}</strong> {t("preview.medical.requestTextDays")}{" "}
        <strong>{startDateText}</strong> {t("preview.medical.requestTextTo")} <strong>{endDateText}</strong>.
      </p>
      {formData.diagnosis && (
        <p className="mt-4">
          {t("preview.medical.diagnosis")}: <strong>{diagnosis}</strong>
        </p>
      )}
    </LeavePreviewLayout>
  );
}
